
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { AlertCircle, CheckCircle2, XCircle, HelpCircle, ShieldAlert, ShieldCheck, History } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface ScopeSignal {
  id: string;
  item: string;
  state: 'contracted' | 'accepted' | 'discussed' | 'out_of_scope';
  source: 'sow' | 'sales_notes' | 'manual';
  resolvedBy?: string;
  resolvedAt?: string;
  rationale?: string;
}

interface ScopeSignalsPanelProps {
  signals: ScopeSignal[];
  userRole: 'consultant' | 'client';
  companyId: string;
  accessToken: string;
  onUpdate?: () => void;
}

export function ScopeSignalsPanel({ signals, userRole, companyId, accessToken, onUpdate }: ScopeSignalsPanelProps) {
  const [selectedSignal, setSelectedSignal] = useState<ScopeSignal | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [rationale, setRationale] = useState('');
  const [actionType, setActionType] = useState<'accept' | 'reject' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Group signals
  const contracted = signals.filter(s => s.state === 'contracted');
  const accepted = signals.filter(s => s.state === 'accepted');
  const discussed = signals.filter(s => s.state === 'discussed');
  const outOfScope = signals.filter(s => s.state === 'out_of_scope');

  const handleOpenResolve = (signal: ScopeSignal, type: 'accept' | 'reject') => {
    setSelectedSignal(signal);
    setActionType(type);
    setRationale('');
    setResolveDialogOpen(true);
  };

  const handleConfirmResolution = async () => {
    if (!selectedSignal || !actionType) return;
    if (!rationale.trim()) {
        toast.error("Rationale is required.");
        return;
    }

    setIsSubmitting(true);
    try {
        const newState = actionType === 'accept' ? 'accepted' : 'out_of_scope';
        
        const response = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/engagement/${companyId}/scope`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    signalId: selectedSignal.id,
                    itemText: selectedSignal.item,
                    source: selectedSignal.source,
                    state: newState,
                    rationale
                })
            }
        );

        if (response.ok) {
            toast.success(newState === 'accepted' ? "Scope item accepted" : "Scope item rejected");
            setResolveDialogOpen(false);
            if (onUpdate) onUpdate();
        } else {
            const err = await response.json();
            toast.error(err.error || "Failed to update scope");
        }
    } catch (error) {
        console.error("Error resolving scope:", error);
        toast.error("Network error");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-l-4 border-l-blue-500 bg-white">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
            <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <ShieldCheck className="size-5 text-blue-600" />
                    Engagement Scope & Guardrails
                </CardTitle>
                <CardDescription>
                    Official scope definition and resolution of ambiguity
                </CardDescription>
            </div>
            {userRole === 'consultant' && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    Consultant View
                </Badge>
            )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
            
            {/* 1. APPROVED WORK (Contracted + Accepted) */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                    <CheckCircle2 className="size-3 text-green-600" />
                    Approved for Delivery
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {contracted.map(s => (
                        <div key={s.id} className="flex items-center gap-2 text-sm bg-green-50/50 p-2 rounded border border-green-100 text-green-900">
                             <Badge className="bg-green-600 text-white hover:bg-green-700 text-[10px] h-5 px-1.5">SOW</Badge>
                             {s.item}
                        </div>
                    ))}
                    {accepted.map(s => (
                        <div key={s.id} className="flex items-center justify-between gap-2 text-sm bg-blue-50/50 p-2 rounded border border-blue-100 text-blue-900">
                             <div className="flex items-center gap-2">
                                <Badge className="bg-blue-600 text-white hover:bg-blue-700 text-[10px] h-5 px-1.5">ACCEPTED</Badge>
                                {s.item}
                             </div>
                             {userRole === 'consultant' && s.rationale && (
                                 <div className="group relative">
                                    <History className="size-3 text-blue-400 cursor-help" />
                                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                                        <p className="font-bold mb-1">Accepted Rationale:</p>
                                        {s.rationale}
                                    </div>
                                 </div>
                             )}
                        </div>
                    ))}
                    {(contracted.length === 0 && accepted.length === 0) && (
                        <div className="text-sm text-gray-400 italic">No scope items defined yet.</div>
                    )}
                </div>
            </div>

            {/* 2. AMBIGUITY (Discussed) - Only visible to Consultant if role is protected, 
                BUT prompt says Clients can see "Discussed" as "Not Yet Accepted"? 
                Prompt: "Clients... Accepted Scope... Contracted scope... discussed (Not Yet Accepted)"
                Actually Prompt says: "In the Engagement Plan... Display scope items grouped by... Discussed"
                BUT ALSO: "Discussed (not accepted) items... May appear as Potential Future Work"
                AND "Clients... Cannot see... Consultant-only rationale"
            */}
            {(discussed.length > 0) && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h4 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                        <HelpCircle className="size-3 text-amber-500" />
                        Discussed / Potential Scope (Not Contracted)
                    </h4>
                    <div className="space-y-2">
                        {discussed.map(s => (
                            <div key={s.id} className="flex items-center justify-between text-sm bg-amber-50/30 p-2 rounded border border-amber-100">
                                <div className="flex items-center gap-2 text-amber-900">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                                    {s.item}
                                </div>
                                {userRole === 'consultant' && (
                                    <div className="flex items-center gap-2">
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-6 px-2 text-xs text-green-700 hover:text-green-800 hover:bg-green-50"
                                            onClick={() => handleOpenResolve(s, 'accept')}
                                        >
                                            Accept
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-6 px-2 text-xs text-red-700 hover:text-red-800 hover:bg-red-50"
                                            onClick={() => handleOpenResolve(s, 'reject')}
                                        >
                                            Reject
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 3. OUT OF SCOPE - Visible to both to avoid confusion */}
            {(outOfScope.length > 0) && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h4 className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                        <XCircle className="size-3 text-gray-400" />
                        Explicitly Out of Scope
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {outOfScope.map(s => (
                            <div key={s.id} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded border border-gray-200 line-through decoration-gray-400">
                                {s.item}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
      </CardContent>

      {/* RESOLUTION DIALOG (Consultant Only) */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>
                    {actionType === 'accept' ? 'Accept Scope Item' : 'Mark as Out of Scope'}
                </DialogTitle>
                <DialogDescription>
                    {actionType === 'accept' 
                        ? `Promote "${selectedSignal?.item}" to Accepted Scope. This authorizes work on this item for this engagement.`
                        : `Demote "${selectedSignal?.item}" to Out of Scope. This explicitly excludes it from the engagement plan.`
                    }
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">
                        Rationale <span className="text-red-500">*</span>
                    </label>
                    <Textarea 
                        placeholder={actionType === 'accept' 
                            ? "e.g. Critical dependency for Incident Management workflow..." 
                            : "e.g. Not included in SOW pricing, requires separate change order..."
                        }
                        value={rationale}
                        onChange={(e) => setRationale(e.target.value)}
                        className="min-h-[100px]"
                    />
                    <p className="text-xs text-muted-foreground">
                        This rationale is visible to other consultants but hidden from the client.
                    </p>
                </div>
            </div>

            <DialogFooter>
                <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
                <Button 
                    variant={actionType === 'accept' ? 'default' : 'destructive'}
                    onClick={handleConfirmResolution}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <span className="animate-spin mr-2">...</span> : null}
                    Confirm Resolution
                </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
